import classNames from "classnames"
import { useRef, useState, useCallback } from "react"
import { SalaryCalculation } from "@oysterhr/schema"
import { useTranslation } from "react-i18next"
import type { TFunction } from "react-i18next"
import { CalculationCard } from "../CalculationCard"
import { TotalHiringCost } from "../TotalHiringCost"
import { useCalculationsProvider } from "../../contexts"

interface CalculationsListProps {
  calculations: Array<SalaryCalculation>
}

interface HiresInfoSectionProps {
  t: TFunction
  numOfHires: number
  toggleForm: (isOpen: boolean) => void
}

const HiresInfoSection = ({ t, numOfHires, toggleForm }: HiresInfoSectionProps) => (
  <div className="flex flex-row items-center justify-between border-gray-200 bg-gray-100 px-4 py-3 text-left">
    <span className="text-sm font-semibold">
      {t("numOfHires")} {numOfHires}
    </span>
    <button className="text-sm text-primary-base underline" onClick={() => toggleForm(true)}>
      {t("addAnotherHire")}
    </button>
  </div>
)

export const CalculationsList = ({ calculations }: CalculationsListProps) => {
  const {
    cycle,
    activeCalculationIndex,
    setActiveCalculationIndex,
    companyCurrencyCode,
    setIsCalculationFormOpen,
  } = useCalculationsProvider()
  const [isMobileListExpanded, setIsMobileListExpanded] = useState(false)
  const { t } = useTranslation("CostCalculator")

  const changeActiveCalculation = useCallback(
    (calculationIndex: number) => {
      if (calculationIndex !== activeCalculationIndex) {
        setActiveCalculationIndex(calculationIndex)
      }
      // In the mobile view, we also want to close the menu after the user selects a calculation
      setIsMobileListExpanded(false)
    },
    [setActiveCalculationIndex, activeCalculationIndex],
  )

  const toggleMobileList = useCallback(() => {
    setIsMobileListExpanded(prev => !prev)
  }, [])

  const totalHiringCostContainerRef = useRef<HTMLDivElement>(null)

  return (
    <>
      {/* Desktop version of the list */}
      <div className="col-span-1 hidden h-fit flex-col rounded-md border border-gray-200 lg:flex">
        <HiresInfoSection
          t={t}
          numOfHires={calculations.length}
          toggleForm={setIsCalculationFormOpen}
        />
        <div className="overflow-hidden">
          {calculations.map((calculation, index) => (
            <CalculationCard
              index={index}
              isActive={index === activeCalculationIndex}
              onClick={changeActiveCalculation}
              key={index}
              calculation={calculation}
            />
          ))}
        </div>
        <TotalHiringCost
          companyCurrencyCode={companyCurrencyCode}
          calculations={calculations}
          cycle={cycle}
        />
      </div>
      {/* Mobile version of the list */}
      {/* Overlay that appears below the menu when it's opened */}
      {isMobileListExpanded && (
        <button
          onClick={toggleMobileList}
          className="fixed bottom-0 left-0 right-0 top-0 z-10 bg-black opacity-50"
          data-testid="mobile-overlay"
        />
      )}
      <div className="fixed bottom-0 left-0 right-0 z-10 lg:hidden">
        <div
          /* Magic number. The max height of the list should be the viewport - the size of the navbar - the size of the TotalHiringCost component */
          style={{
            maxHeight: `calc(100vh - 4rem - ${totalHiringCostContainerRef.current?.clientHeight}px)`,
          }}
          className={classNames("max-h-screen overflow-auto", {
            hidden: !isMobileListExpanded,
            "block flex-col": isMobileListExpanded,
          })}
        >
          {isMobileListExpanded && (
            <HiresInfoSection
              t={t}
              numOfHires={calculations.length}
              toggleForm={setIsCalculationFormOpen}
            />
          )}
          {calculations.map((calculation, index) => (
            <CalculationCard
              index={index}
              isActive={index === activeCalculationIndex}
              onClick={changeActiveCalculation}
              key={index}
              calculation={calculation}
            />
          ))}
        </div>
        {!isMobileListExpanded && (
          <HiresInfoSection
            t={t}
            numOfHires={calculations.length}
            toggleForm={setIsCalculationFormOpen}
          />
        )}
        <TotalHiringCost
          containerRef={totalHiringCostContainerRef}
          companyCurrencyCode={companyCurrencyCode}
          toggleMobileList={toggleMobileList}
          calculations={calculations}
          cycle={cycle}
          isMobileListExpanded={isMobileListExpanded}
        />
      </div>
    </>
  )
}
